import {
  parseISO,
  parse,
  startOfDay,
  isToday,
  isTomorrow,
  format,
  isBefore,
  isAfter
} from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'

const DEFAULT_TIMEZONE = 'Europe/London'
/** TIME UNITS in ms - as that's the way JS likes it */
export const TIME_UNITS = {
  SECOND: 1 * 1000,
  MINUTE: 60 * 1000,
  HOUR: 60 * 60 * 1000,
  DAY: 24 * 60 * 60 * 1000
} as const

export const timezoneNow = (timezone = DEFAULT_TIMEZONE): Date => {
  const now = new Date()
  return utcToZonedTime(now.toISOString(), timezone)
}

export const parseTimezoneISO = (
  iso: string,
  timezone = DEFAULT_TIMEZONE
): Date => {
  return utcToZonedTime(parseISO(iso).toISOString(), timezone)
}

export const timeToDatetime = (
  time: string,
  timezone = DEFAULT_TIMEZONE,
  timeFormat: string = 'HH:mm'
) => {
  return parse(time, timeFormat, timezoneNow(timezone))
}

// Bec safari 💩
export const createDate = (date: string | Date | null): Date => {
  if (!date) return parseISO(new Date().toISOString())
  if (typeof date === 'string') return parseISO(date)
  return parseISO(date.toISOString())
}

export const zonedToLocalStartOfDay = (date: Date | null) => {
  if (!date) return null

  const localDate = parseISO(date.toISOString())
  const dayStartZoned = startOfDay(localDate)

  return dayStartZoned
}

/** Standardised format of date strings
 * short - Wed 01 Apr
 * long - Wednesday 1st April
 */
export const createReadableDateString = (
  date: Date,
  dateFormat: 'short' | 'long'
) => {
  if (isToday(date)) {
    return 'Today'
  }

  if (isTomorrow(date)) {
    return 'Tomorrow'
  }

  let formatOptions = 'EEE dd MMM'
  if (dateFormat === 'short') formatOptions = 'EEE do MMM' // e.g. Tue 2nd Sep
  if (dateFormat === 'long') formatOptions = 'EEEE do MMMM' // e.g. Tuesday 2nd September

  return format(date, formatOptions)
}

/** Using date-fns format, covert a Time string ("HH:mm:ss" = "16:45:00") into desired Time format.
 * e.g. ("HH" = "16"), ("mm:ss" = "45:00").
 *
 * !! Do not use for days as it will default to "Today" !!
 */
export const formatTimeString = (
  time: string | null,
  formatOptions: string
) => {
  if (!time) return '--:--'
  const datetime = timeToDatetime(time, undefined, 'HH:mm:ss')

  return format(datetime, formatOptions)
}

/** Compare two dates and return helpful dictations about their comparison. */
export const compareDatetimes = (
  datetimeA: Date | string,
  datetimeB: Date | string
) => {
  let tempDatetimeA
  let tempDatetimeB
  if (typeof datetimeA === 'string') {
    tempDatetimeA = new Date(datetimeA)
  } else tempDatetimeA = datetimeA
  if (typeof datetimeB === 'string') {
    tempDatetimeB = new Date(datetimeB)
  } else tempDatetimeB = datetimeB

  const formattedDateStringA = format(tempDatetimeA, 'yyyy-MM-dd')
  const formattedDateStringB = format(tempDatetimeB, 'yyyy-MM-dd')
  const formattedTimeStringA = format(tempDatetimeA, 'HH:mm:ss')
  const formattedTimeStringB = format(tempDatetimeB, 'HH:mm:ss')

  return {
    isBefore: isBefore(tempDatetimeA, tempDatetimeB),
    isAfter: isAfter(tempDatetimeA, tempDatetimeB),
    isDateEqual: formattedDateStringA === formattedDateStringB,
    isTimeEqual: formattedTimeStringA === formattedTimeStringB
  }
}
